import React, { useState } from 'react';

import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { hashHistory } from 'react-router';

import {
  InlineEditableField,
  List,
  Select,
  Switch,
  Typography,
} from '../../../../../components/atoms';
import { ListItem as ProfileListItem } from '../../../../../components/atoms/List';
import {
  ArrowFilledDown as ArrowFilledDownIcon,
  Card as CardIcon,
  ChevronRight as ChevronRightIcon,
  Email as EmailIcon,
  Label as LabelIcon,
  Language as LanguageIcon,
  Lock as LockIcon,
  Login as LoginIcon,
  Message as MessageIcon,
  Profile as ProfileIcon,
  Secured as SecuredIcon,
} from '../../../../../components/icons';
import { ExpandSection } from '../../../../../components/molecules';
import { ProfileActionsType } from '../../../../../types/menu';
import { Track } from '../../../../../weborder/analytics';
import * as Api from '../../../../../weborder/Api';
import { cookieConsent } from '../../../../../weborder/cookieconsent';
import { Utilities } from '../../../../../weborder/Utilities';
import { menuUtils } from '../../../utils';

type ParamProps = {
  id: number;
  type: 'collection' | 'delivery';
  vrid: number;
};

type Props = {
  actions: ProfileActionsType;
  configData: Api.AppConfig;
  contactFormType: Api.ContactFormType;
  customerData: Api.Customer;
  hasDisableNonEssentialCookies: boolean;
  hasGdprCookieFeatureFlag: boolean;
  languageCode: string;
  panelState?: boolean;
  params: ParamProps;
  translate: any;
};

const useStyles = makeStyles((theme: Theme) => ({
  customerProfilePartial: {
    backgroundColor: 'transparent',
    padding: theme.spacing(3, 0),
  },

  languageSelect: {
    '& .fd-icon': {
      position: 'absolute',
      right: 0,
      pointerEvents: 'none',
    },
  },

  profileList: {
    padding: theme.spacing(0, 3, 0, 2),
    width: '100%',

    '& .MuiListItem-root': {
      paddingBottom: theme.spacing(1.5),
      paddingTop: theme.spacing(1.5),
    },

    '& .MuiListItem-root:last-of-type': {
      borderBottomColor: 'transparent',

      '&:hover': {
        borderBottom: `1px solid ${theme.palette.primary.main}`,
      },
    },
  },

  [theme.breakpoints.up('sm')]: {
    customerProfilePartial: {},

    profileList: {
      padding: 0,
    },
  },
}));

export function CustomerProfilePartial(props: Props): JSX.Element {
  const classes = useStyles();
  const {
    actions,
    configData,
    contactFormType,
    customerData,
    hasDisableNonEssentialCookies,
    hasGdprCookieFeatureFlag,
    languageCode,
    panelState = false,
    params,
    translate,
  } = props;

  const [expandedState, setExpandedState] = useState(panelState);

  const theme = useTheme();
  const isDesktopLayout = useMediaQuery(theme.breakpoints.up('lg'));

  const toggleExpandedState = () => {
    setExpandedState(!expandedState);

    if (!isDesktopLayout && !expandedState) {
      menuUtils.handleScrollToSection('profile-panel');
    }
  };

  const getAvailableLanguageOptions = () => {
    const langOptions = {} as any;
    configData.Languages.forEach(
      (language: Api.Language) =>
        (langOptions[language.LangageId] = language.Name),
    );
    return langOptions;
  };

  const handleEmailValidation = (value: unknown): boolean => {
    if (typeof value !== 'string') {
      return false;
    }

    return Utilities.isValidEmail(value);
  };
  const handleNavigateTo = (targetUrl) => {
    hashHistory.push(targetUrl);
  };
  const handlePromotions = () => {
    Track('Profile', 'Receive Promotions', {
      event_label: (!customerData.SmsPromotionsEnabled).toString(),
    });
    actions.onChangePromotions(!customerData.SmsPromotionsEnabled);
  };
  const handlePrivacySettings = () => cookieConsent.edit();

  const hasLanguages = configData.Languages.length > 1;
  const hasContactUs = contactFormType === Api.ContactFormType.Intercom;
  const hasPrivacySettings =
    hasGdprCookieFeatureFlag &&
    !hasDisableNonEssentialCookies &&
    !Utilities.extraOption('data-is-elixir-site');

  const profileItems = {
    customerName: {
      action: () => {},
      dataFd: 'profile-name',
      label: (
        <InlineEditableField
          action={actions.onChangeName}
          value={customerData.CustomerName}
        />
      ),
      leftIcon: <ProfileIcon />,
      rightIcon: null,
    },
    customerEmail: {
      action: () => {},
      dataFd: 'profile-email',
      label: (
        <InlineEditableField
          action={actions.onChangeEmail}
          validation={handleEmailValidation}
          value={customerData.EmailAddress}
        />
      ),
      leftIcon: <EmailIcon />,
      rightIcon: null,
    },
    paymentMethods: {
      action: () =>
        handleNavigateTo(`/payment/${params.vrid}/${params.type}/${params.id}`),
      dataFd: 'profile-payment-methods',
      label: translate('Payment_methods'),
      leftIcon: <CardIcon />,
      rightIcon: <ChevronRightIcon />,
    },
    receivePromotions: {
      action: handlePromotions,
      dataFd: 'profile-receive-promotions',
      label: translate('Receive_promotions'),
      leftIcon: <LabelIcon />,
      rightIcon: <Switch checked={customerData.SmsPromotionsEnabled} />,
    },
    languageSelector: {
      action: () => {},
      dataFd: 'profile-language-selector',
      label: (
        <Select
          aria-label={translate('Select_language')}
          id="language-selector"
          IconComponent={ArrowFilledDownIcon}
          isNative={true}
          menuItems={getAvailableLanguageOptions()}
          onChange={(event) =>
            actions.onChangeLanguage(event.target.value as string)
          }
          value={languageCode}
        />
      ),
      leftIcon: <LanguageIcon />,
      rightIcon: null,
    },
    contactUs: {
      action: actions.onContactUs,
      dataFd: 'profile-contact-us',
      label: translate('Contact_us'),
      leftIcon: <MessageIcon />,
      rightIcon: null,
    },
    privacySettings: {
      action: handlePrivacySettings,
      dataFd: 'profile-privacy-settings',
      label: translate('Privacy_Settings'),
      leftIcon: <LockIcon />,
      rightIcon: <ChevronRightIcon />,
    },
    aboutData: {
      action: actions.onAboutMyData,
      dataFd: 'profile-about-data',
      label: translate('About_my_data'),
      leftIcon: <SecuredIcon />,
      rightIcon: <ChevronRightIcon />,
    },
    logout: {
      action: actions.onLogOut,
      dataFd: 'profile-logout',
      label: translate('Log_out'),
      leftIcon: <LoginIcon />,
      rightIcon: null,
    },
  };

  if (!hasLanguages) {
    delete profileItems.languageSelector;
  }
  if (!hasContactUs) {
    delete profileItems.contactUs;
  }
  if (!hasPrivacySettings) {
    delete profileItems.privacySettings;
  }

  const items: ProfileListItem[] = Object.values(profileItems).map((item) => {
    return {
      leftIcon: item.leftIcon,
      onClick: item.action,
      primaryContent: item.label,
      rightIcon: item.rightIcon,
      testId: item.dataFd,
    };
  });

  const sectionNameElement = (
    <Typography variant="h3">{translate('Menu_Profile_Title')}</Typography>
  );

  return (
    <ExpandSection
      expanded={expandedState}
      className={classes.customerProfilePartial}
      id="profile-panel"
      onChange={toggleExpandedState}
      sectionDescription={customerData.CustomerPhoneNumberLocalFormatString}
      sectionName={sectionNameElement}
      TransitionProps={{
        mountOnEnter: true,
        timeout: 0,
        unmountOnExit: true,
      }}
      translate={translate}
    >
      <List className={classes.profileList} items={items} />
    </ExpandSection>
  );
}
